import { motion } from "framer-motion";
import { MapPin, Phone, MessageCircle } from "lucide-react";
import { SITE } from "@/data/site";
import SectionLabel from "./SectionLabel";

export default function Contact({ onBook }) {
  const waLink = `https://wa.me/${SITE.phoneIntl.replace("+", "")}?text=${encodeURIComponent(SITE.whatsappMessage)}`;
  const telLink = `tel:${SITE.phoneIntl}`;

  return (
    <section
      id="contact"
      className="relative py-24 md:py-32 bg-[#F9F6F0]"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionLabel
          eyebrow="Contact"
          title={<>Nous trouver<br /><span className="italic text-[#8B5A2B]">à Montélimar</span></>}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="flex items-start gap-5">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#EFEBE3] flex items-center justify-center">
                <MapPin strokeWidth={1.2} className="w-5 h-5 text-[#8B5A2B]" />
              </div>
              <div>
                <p className="text-xs tracking-[0.3em] uppercase text-[#8B5A2B] font-body mb-1">
                  Adresse
                </p>
                <p className="font-display text-2xl text-stone-900 leading-snug">
                  {SITE.address}
                </p>
                <p className="text-stone-500 font-light mt-1">France</p>
              </div>
            </div>

            <div className="flex items-start gap-5">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#EFEBE3] flex items-center justify-center">
                <Phone strokeWidth={1.2} className="w-5 h-5 text-[#8B5A2B]" />
              </div>
              <div>
                <p className="text-xs tracking-[0.3em] uppercase text-[#8B5A2B] font-body mb-1">
                  Téléphone
                </p>
                <a
                  href={telLink}
                  className="font-display text-2xl text-stone-900 hover:text-[#8B5A2B] transition-colors"
                  data-testid="contact-phone-link"
                >
                  {SITE.phone}
                </a>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#25D366] text-white font-body font-medium hover:bg-[#1ebe57] transition-all duration-300 hover:-translate-y-0.5 shadow-md shadow-emerald-500/20"
                data-testid="contact-whatsapp-btn"
              >
                <MessageCircle strokeWidth={1.8} className="w-4 h-4" />
                WhatsApp
              </a>
              <a
                href={telLink}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-stone-900 text-[#F9F6F0] font-body font-medium hover:bg-stone-800 transition-all duration-300 hover:-translate-y-0.5"
                data-testid="contact-call-btn"
              >
                <Phone strokeWidth={1.8} className="w-4 h-4" />
                Appeler
              </a>
              <button
                onClick={onBook}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-stone-900 text-stone-900 font-body font-medium hover:bg-stone-900 hover:text-[#F9F6F0] transition-all duration-300"
                data-testid="contact-book-btn"
              >
                Réserver
              </button>
            </div>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative rounded-2xl overflow-hidden border border-[#E6DFD3] shadow-[0_10px_60px_rgba(28,25,23,0.08)] aspect-[4/3] lg:aspect-auto lg:h-[480px]"
          >
            <iframe
              title="Localisation Harmony Massage Montélimar"
              src={SITE.mapsEmbed}
              width="100%"
              height="100%"
              style={{ border: 0, filter: "grayscale(0.3) contrast(0.95)" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
